import React, { useState } from 'react';
import { FlaskConical, Syringe, Droplet, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react'; // Updated icon imports
import { VialMetrics } from '../types/calculator';

interface ResultCardProps {
  metrics: VialMetrics;
}

export const ResultCard: React.FC<ResultCardProps> = ({ metrics }) => {
  const [showDetails, setShowDetails] = useState(true);

  const results = [
    {
      label: 'Optimal Reconstitution',
      value: `${metrics.reconstitutionAmount} ${metrics.reconstitutionUnits}`,
      icon: <FlaskConical className="text-blue-500 w-6 h-6" />,
    },
    {
      label: 'Final Concentration',
      value: `${metrics.finalConcentration} mg/ml`,
      icon: <Droplet className="text-purple-500 w-6 h-6" />,
    },
    {
      label: 'Units Per Dose',
      value: metrics.unitsPerDose,
      icon: <Syringe className="text-green-500 w-6 h-6" />,
    },
    {
      label: 'Dose in Milliliters',
      value: `${metrics.doseMilliliters} ml`,
      icon: <Droplet className="text-indigo-500 w-6 h-6" />,
    },
    {
      label: 'Total Doses Available',
      value: metrics.totalDoses,
      icon: <CheckCircle className="text-teal-500 w-6 h-6" />,
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 w-full">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Calculation Results</h2>

      {/* Narrative Display */}
      <p className="text-gray-700 text-lg leading-relaxed">
        <FlaskConical className="inline text-blue-500 w-6 h-6 mr-2" />
        Reconstitute the vial with 
        <strong className="text-gray-900"> {metrics.reconstitutionAmount} {metrics.reconstitutionUnits}</strong>, 
        resulting in a final concentration of 
        <Droplet className="inline text-purple-500 w-6 h-6 mx-2" />
        <strong className="text-gray-900"> {metrics.finalConcentration} mg/ml</strong>. 
        Each dose delivers 
        <Syringe className="inline text-green-500 w-6 h-6 mx-2" />
        <strong className="text-gray-900"> {metrics.unitsPerDose} units</strong>, 
        requiring 
        <Droplet className="inline text-indigo-500 w-6 h-6 mx-2" />
        <strong className="text-gray-900"> {metrics.doseMilliliters} ml</strong>. 
        With this preparation, you will have a total of 
        <CheckCircle className="inline text-teal-500 w-6 h-6 mx-2" />
        <strong className="text-gray-900"> {metrics.totalDoses} doses</strong> available.
      </p>

      {/* Show More Toggle */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center text-blue-600 mt-4 focus:outline-none"
      >
        {showDetails ? (
          <>
            <ChevronUp className="w-5 h-5 mr-2" />
            Show Less
          </>
        ) : (
          <>
            <ChevronDown className="w-5 h-5 mr-2" />
            Show More
          </>
        )}
      </button>

      {/* Detailed List */}
      {showDetails && (
        <div className="mt-4 grid gap-4">
          {results.map((result) => (
            <div
              key={result.label}
              className="flex justify-between items-center py-2 border-b border-gray-100"
            >
              <div className="flex items-center">
                {result.icon}
                <span className="ml-3 text-gray-600">{result.label}</span>
              </div>
              <span className="font-medium text-gray-900">{result.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
